// Libraries
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { isNotNil } from 'ramda';

// MUI
import { Container } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

// Core
import {
    IBoundaryValue,
    ParameterValues,
    paramRepository,
    ResourceTextApplication,
} from '../../core/resources';
import log from '../../core/log';
import utils from '../../core/utils';
import auth from '../../core/authorization';
import { boundaryRepository } from '../../core/boundaries';

// Common
import { ViewTitle } from '../../common/typography';
import ViewLoader from '../../common/ViewLoader';

// Interfaces
import { IFarmDetails } from '../../interfaces/IBusinessEntities';
import {
    AnimalFilterTypeEnum,
    AnimalTypeEnum,
    AnnouncementFlagsEnum,
    AnnouncementListDataGridColumnType,
    AnnouncementTypeEnum,
    DialogModeEnum,
    FarmingTypeEnum,
} from '../../interfaces/enums';
import {
    IAnimalSummaryExtraInfo,
    IFarmAnimal,
    IFillingCapacityStatistic,
    IFilteredAnimalList,
    ILatestBovineRegisterDataSyncRequest,
    INasevaResult,
    INasevaSpecialSituation,
} from '../../interfaces/IAnimal';
import { IAnimalAnnouncement, ICreatePreliminaryPurchaseAnnouncement, IUpdateAnnouncementResult } from '../../interfaces/IAnnouncement';
import { ITiltuApiResult } from '../../interfaces/IGeneral';
import { IMainGroup } from '../../interfaces/IGroup';
import {
    ACarouselAdditionalBoxInfo,
    CarouselInfoEnum,
} from '../../interfaces/animal-carousel/ICarouselInfo';
import { EmptyGUID, GUIDType } from '../../interfaces/types';

// Store
import { useAppSelector } from '../../store/hooks';

// API
import companyApi from '../../api/companyApi';
import animalAnnouncementApi from '../../api/animalAnnouncementApi';
import Api from '../../api/animalListApi';
import AnimalsApi from '../../api/animalsApi';
import NasevaApi from '../../api/nasevaApi';
import groupsApi from '../../api/groupsApi';

// Feature
import AnimalListFilter, { AnimalListFilterProps } from './AnimalListFilter';
import AnimalListGrid, { AnimalListGridProps } from './AnimalListGrid';
import AnnouncementListDataGrid from '../announcements/components/data-grid/announcement-list/AnnouncementListDataGrid';
import {
    getAnimalGroup,
    isAnimalAnnouncementUpdateRestricted,
    mapAnimalAnnouncementAnimalsToIAnimalSummaryExtraInfo,
    omitBovinePurchaseAnnouncements,
} from '../announcements/helpers/data';
import { getFilteredAnimals } from './helper';
import BovineIntermediationAnnouncement from '../announcements/components/bovine-intermediation-announcement/BovineIntermediationAnnouncement';
import BovinePurchaseAnnouncement from '../announcements/components/bovine-purchase-announcement/BovinePurchaseAnnouncement';
import BovineSlaughterAnnouncement from '../announcements/components/bovine-slaughter-announcement/BovineSlaughterAnnouncement';
import { getAnimalCount, getBovinePurchaseAnimalCount } from '../announcements/helpers/animal';
import { mapAnnouncementResult } from '../announcements/components/bovine-slaughter-announcement/helpers/mapper';
import ManualAnnouncementDialog from '../manual-announcements/components/ManualAnnouncementDialog';
import { IAnnouncementListDataGridProps } from '../shared/types/propTypes';
import { fetchCanCreateAnimalAnnouncements } from '../shared/validators/canCreateAnnouncementValidator';
import ConfirmBovinePurchaseAnnouncement from '../announcements/components/bovine-purchase-announcement/confirm/ConfirmBovinePurchaseAnnouncement';
import producerReturnApi from '../../api/producerReturnApi';
import { IProducerReturnSummary } from '../../interfaces/IProducerReturn';
import { NotificationViewContext } from '../../notifications/context/INotificationContext';

export interface ParamFilter {
    paramCode: number;
    subParam: number;
}

interface IConfirmRelatedAnnouncement {
    announcement: IAnimalAnnouncement | null;
    openDialog: boolean;
}

export default function AnimalList() {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaGeneral',
        'AnelmaBovine',
        'AnelmaLayout',
        'AnelmaSolmuErrors',
        'AnelmaNotifications',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [announcementActionLoading, setAnnouncementActionLoading] = useState<boolean>(false);
    const [allAnimalsData, setAllAnimalsData] = useState<IFilteredAnimalList[] | null>(null);
    const [animalData, setAnimalData] = useState<IAnimalSummaryExtraInfo[]>([]);
    const [farmData, setFarmData] = useState<IFarmDetails | null>(null);
    const [animalListFilterProps, setAnimalListFilterProps] =
        useState<AnimalListFilterProps | null>(null);
    const [additionalInfo, setAdditionalInfo] = useState<ACarouselAdditionalBoxInfo[]>([]);
    const [selectedParamFilter, setSelectedParamFilter] = useState<ParamFilter | null>(null);
    const [filteredAnimalData, setFilteredAnimalData] = useState<IAnimalSummaryExtraInfo[] | null>(
        []
    );
    const [animalListGridProps, setAnimalListGridProps] = useState<AnimalListGridProps | null>(
        null
    );
    const [announcementListGridProps, setAnnouncementListGridProps] =
        useState<IAnnouncementListDataGridProps>();
    const [showAnnouncementListGrid, setShowAnnouncementListGrid] = useState<boolean>(false);
    const [animalAnnouncementData, setAnimalAnnouncementData] = useState<IAnimalAnnouncement[]>([]);
    const [announcementDialogVisible, setAnnouncementDialogVisible] = useState<boolean>(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<IAnimalAnnouncement>();
    const [slaughterAnnouncements, setSlaughterAnnouncements] = useState<IAnimalAnnouncement[]>([]);
    const [intermediationAnnouncements, setIntermediationAnnouncements] = useState<
        IAnimalAnnouncement[]
    >([]);
    const [bovinePurchaseAnnouncements, setBovinePurchaseAnnouncements] = useState<
        IAnimalAnnouncement[]
    >([]);
    const [slaughterAnnouncementCount, setSlaughterAnnouncementCount] = useState<number>(0);
    const [intermediationAnnouncementCount, setIntermediationAnnouncementCount] =
        useState<number>(0);
    const [bovinePurchaseAnnouncementCount, setBovinePurchaseAnnouncementCount] =
        useState<number>(0);
    const [slaughterAnnouncementAnimalCount, setSlaughterAnnouncementAnimalCount] =
        useState<number>(0);
    const [intermediationAnnouncementAnimalCount, setIntermediationAnnouncementAnimalCount] =
        useState<number>(0);
    const [bovinePurchaseAnnouncementAnimalCount, setBovinePurchaseAnnouncementAnimalCount] =
        useState<number>(0);

    const [lastSync, setLastSync] = useState<ILatestBovineRegisterDataSyncRequest | null>(null);
    const [nasevaVisits, setNasevaVisits] = useState<INasevaResult[] | null>(null);
    const [animalGroups, setAnimalGroups] = useState<IMainGroup[] | null>(null);
    const [fillingStatistic, setFillingStatistic] = useState<IFillingCapacityStatistic | null>(
        null
    );
    const context = useAppSelector((state) => state.context);
    const culture = useAppSelector((state) =>
        state.configuration?.data?.culture?.supportedCultures?.find((i) => i.selected)
    );
    const [canCreateAnimalAnnouncement, setCanCreateAnimalAnnouncement] = useState<boolean | null>(
        null
    );
    const [chainInformationFound, setChainInformationFound] = useState<boolean>(false);
    const [nasevaSpecialSituations, setNasevaSpecialSituations] = useState<
        INasevaSpecialSituation[] | null
        >(null);
    const [producerReturns, setProducerReturns] = useState<IProducerReturnSummary[]>([]);
    const [announcementNotificationMinBoundary, setAnnouncementNotificationMinBoundary] =
        useState<number>(0);
    const [confirmBovinePurchaseAnnouncement, setConfirmBovinePurchaseAnnouncement] =
        useState<IConfirmRelatedAnnouncement>({ announcement: null, openDialog: false });

    const handleParamFilterChange = (filter: ParamFilter) => {
        setSelectedParamFilter(filter);
    };

    useEffect(() => {
        boundaryRepository.load(['AnelmaNotifications']).then(() => {
            const notificationBoundary = boundaryRepository.resource(
                'AnelmaNotifications',
                'AnnouncementNotification'
            );
            if (notificationBoundary.minValue !== null)
                setAnnouncementNotificationMinBoundary(notificationBoundary.minValue);
        });
    }, []);

    useEffect(() => {
        if (context?.data?.currentContext) {
            setIsLoading(true);
            companyApi
                .getCompanyByContext(context.data.currentContext?.context)
                .then((response) => {
                    if (response?.Entity) setFarmData(response.Entity as IFarmDetails);
                    else setFarmData(null);
                });

            setIsLoading(false);
        } else setFarmData(null);
    }, [context]);

    useEffect(() => {
        if (farmData?.Id) {
            refreshData();
            fetchGroupsData();
            fetchCanCreateAnimalAnnouncements(
                farmData.Id,
                setCanCreateAnimalAnnouncement,
                enqueueSnackbar
            );
        } else {
            setAnimalData([]);
            setAllAnimalsData([]);
            setFilteredAnimalData([]);
            setNasevaVisits(null);
            setNasevaSpecialSituations(null);
            setAnimalGroups([]);
        }
    }, [farmData]);

    useEffect(() => {
        if (selectedParamFilter !== null && allAnimalsData) {
            setAnimalData(
                getFilteredAnimals(allAnimalsData, selectedParamFilter) as IAnimalSummaryExtraInfo[]
            );
        }
    }, [selectedParamFilter]);

    useEffect(() => {
        filterAnimals();
    }, [animalData]);

    useEffect(() => {
        if (animalGroups !== null && allAnimalsData != null && allAnimalsData.length > 0) {
            setAllAnimalsData((previous) => previous === null ? null : previous.map(f => {
                f.Animals = f.Animals.map(a => {
                    let animal = a as IAnimalSummaryExtraInfo;
                    animal.Group = getAnimalGroup(animal.Id, animalGroups);
                    return a;
                });

                return f;
            }));
        }
    }, [animalGroups]);

    useEffect(() => {
        // set animals for grid
        if (selectedParamFilter !== null && allAnimalsData) {
            setAnimalData(
                getFilteredAnimals(allAnimalsData, selectedParamFilter) as IAnimalSummaryExtraInfo[]
            );
        }

        // set waiting animals tab warning icon
        const theFlag = 2;
        let info: ACarouselAdditionalBoxInfo[] = [...additionalInfo];

        if (
            allAnimalsData &&
            allAnimalsData.length &&
            allAnimalsData.length > 0 &&
            allAnimalsData.findIndex(
                (filteredList) => filteredList.FilterType === AnimalFilterTypeEnum.WaitingRoom
            ) != -1 &&
            allAnimalsData.find(
                (filteredList) => filteredList.FilterType === AnimalFilterTypeEnum.WaitingRoom
            )?.Animals &&
            allAnimalsData
                .find(
                    (filteredList) => filteredList.FilterType === AnimalFilterTypeEnum.WaitingRoom
                )
                ?.Animals.findIndex((a) => (a.Flags & theFlag) === theFlag) != -1
        ) {
            if (info.findIndex((i) => i.BoxId == 1) == -1) {
                info.push({
                    BoxId: 1,
                    IconInfo: { Info: CarouselInfoEnum.MissingPurchaseDate },
                } as ACarouselAdditionalBoxInfo);
                setAdditionalInfo(info);
            }
        } else {
            info = info.filter((i) => i.IconInfo.Info !== CarouselInfoEnum.MissingPurchaseDate);
            setAdditionalInfo(info);
        }
    }, [allAnimalsData]);

    const refreshData = () => {
        if (farmData?.Id) {
            setIsLoading(true);

            AnimalsApi.getLatestBovineRegisterDataSyncRequest(farmData.Id, true)
                .then((response) => {
                    if (response?.Entity)
                        setLastSync(
                            (response.Entity as ILatestBovineRegisterDataSyncRequest) || null
                        );
                })
                .catch(() => handleShowSnackbarGeneralError());

            NasevaApi.getLatestAnnualVisits(farmData.Id, culture?.value).then((response) => {
                if (!response) {
                    handleShowSnackbarGeneralError();
                    setNasevaVisits(null);
                } else {
                    setNasevaVisits(response.Items);
                }
            });

            NasevaApi.getSpecialSituations({
                FarmsGuIds: [
                    {
                        Id: farmData.Id,
                    },
                ],
                Culture: culture?.value ?? 'fi-FI',
            }).then((response) => {
                if (!response) {
                    handleShowSnackbarGeneralError();
                    setNasevaSpecialSituations(null);
                } else {
                    setNasevaSpecialSituations(response.Items);
                }
            });

            if (
                farmData.ProductionLines.findIndex(
                    (p) =>
                        p.Type == AnimalTypeEnum.Bovine &&
                        p.FarmingTypes.findIndex((t) => t == FarmingTypeEnum.Upbringing) != -1
                ) != -1 &&
                auth.canViewFillingStatistic
            ) {
                Api.getFillingStatistic(farmData.Id)
                    .then((response) => {
                        if (response && response.Entity && response.Entity.CanShowFillingStatistic)
                            setFillingStatistic(response.Entity);
                        else setFillingStatistic(null);
                    })
                    .catch(() => handleShowSnackbarGeneralError());
            } else {
                setFillingStatistic(null);
            }

            Api.getFilteredAnimals(farmData.Id)
                .then((response) => {
                    if (response && response?.Items?.length > 0) {
                        setAllAnimalsData(response.Items);

                        // fetch announcements,
                        // they require allAnimalsData
                        initialAnnouncementDataFetch();
                    }
                })
                .catch(() => handleShowSnackbarGeneralError())
                .finally(() => setIsLoading(false));
        }
    };

    const filterAnimals = () => {
        setFilteredAnimalData(countExternalFields(animalData));
    };

    useEffect(() => {
        setAnimalListGridProps({
            allAnimalsData: allAnimalsData,
            animalData:
                filteredAnimalData && filteredAnimalData.length > 0 ? filteredAnimalData : [],
            animalGroups: animalGroups,
            setAnimalGroups: setAnimalGroups,
            selectedParamFilter: selectedParamFilter,
            isLoading: isLoading,
            handleAnimalAnnouncementMapToState: (data: any, type: AnnouncementTypeEnum) =>
                mapNewAnnouncementToState(data, type),
            mapUpdatedSalesTypeToAllAnimalsData: (data: IFarmAnimal[]) =>
                handleUpdatedSalesType(data),
            processAnnouncementDelete: handleAnnouncementDeletionFromTiltuById,
            farmAnnouncements: animalAnnouncementData,
            farmData: farmData ?? null,
            producerReturns: producerReturns,
            canCreateAnimalAnnouncement: canCreateAnimalAnnouncement,
            removeAnimal: removeAnimal,
            removeAnimalsFromWaitingRoom: removeAnimalsFromWaitingRoom,
            refreshAnnouncements: refreshAnnouncements,
        });
    }, [
        filteredAnimalData,
        isLoading,
        selectedParamFilter,
        animalAnnouncementData,
        canCreateAnimalAnnouncement,
    ]);

    useEffect(() => {
        setAnimalListFilterProps({
            additionalInfo: additionalInfo,
            allAnimalsData: allAnimalsData ?? [],
            allAnnouncementsData: animalAnnouncementData ?? [],
            farmData: farmData ?? null,
            onFilterChange: (filter: ParamFilter) => handleParamFilterChange(filter),
            isLoading: isLoading,
            onIsLoadingChange: (loading: boolean) => setIsLoading(loading),
            lastSync: lastSync,
            nasevaVisits: nasevaVisits,
            fillingStatistic: fillingStatistic,
            refreshData: refreshData,
            animalAnnouncementCount: slaughterAnnouncementCount + intermediationAnnouncementCount,
            slaughterAnnouncementCount: slaughterAnnouncementCount,
            intermediationAnnouncementCount: intermediationAnnouncementCount,
            bovinePurchaseAnnouncementCount: bovinePurchaseAnnouncementCount,
            slaughterAnnouncementAnimalCount: slaughterAnnouncementAnimalCount,
            intermediationAnnouncementAnimalCount: intermediationAnnouncementAnimalCount,
            bovinePurchaseAnnouncementAnimalCount: bovinePurchaseAnnouncementAnimalCount,
            nasevaSpecialSituations: nasevaSpecialSituations,
        });
    }, [
        additionalInfo,
        allAnimalsData,
        animalAnnouncementData,
        farmData,
        isLoading,
        lastSync,
        nasevaVisits,
        fillingStatistic,
        slaughterAnnouncementCount,
        intermediationAnnouncementCount,
        bovinePurchaseAnnouncementCount,
        slaughterAnnouncementAnimalCount,
        intermediationAnnouncementAnimalCount,
        bovinePurchaseAnnouncementAnimalCount,
        nasevaSpecialSituations,
        // watching other than used parameters
        slaughterAnnouncements,
        intermediationAnnouncements,
        bovinePurchaseAnnouncements,
    ]);

    useEffect(() => {
        const data = filteredAnnouncementData();

        if (data) {
            setAnnouncementListGridProps({
                handleModification: (item, chainInformationFound) =>
                    handleAnnouncementDialogVisible(
                        data.find((x) => x.Id === item.Id) as IAnimalAnnouncement,
                        chainInformationFound
                    ),
                handleAnnouncementDeletionFromTiltu: (announcementNumber) => {
                    handleAnnouncementDeletionFromTiltu(announcementNumber);
                },
                handleAnnouncementDeletionFromTiltuById: (announcementId) => {
                    handleAnnouncementDeletionFromTiltuById(announcementId);
                },
                getRelatedSales: getRelatedAnnouncement,
                handleAnnouncementReload: handleAnnouncementReload,
                handleProducerReturnsDeletion: handleProducerReturnsDeletion,
                data: data,
                producerReturns: producerReturns,
                handleSelectedAnnouncement: handleSelectedAnnouncement,
                columnType: announcementListGridColumnType(),
                canCreateAnimalAnnouncement: canCreateAnimalAnnouncement,
            });
        }
    }, [
        canCreateAnimalAnnouncement,
        selectedParamFilter,
        selectedParamFilter?.paramCode,
        selectedParamFilter?.subParam,
        animalAnnouncementData,
        producerReturns,
        bovinePurchaseAnnouncements]);

    useEffect(() => {
        let slaughterAnnouncements: IAnimalAnnouncement[] = [];
        let intermediationAnnouncements: IAnimalAnnouncement[] = [];
        let bovinePurchaseAnnouncements: IAnimalAnnouncement[] = [];

        if (animalAnnouncementData !== null) {
            checkNeedForPreliminaryAnimalAnnouncementWarnings(animalAnnouncementData);
            for (const announcement of animalAnnouncementData) {
                switch (announcement.Type as AnnouncementTypeEnum) {
                    case AnnouncementTypeEnum.Slaughter:
                    case AnnouncementTypeEnum.SlaughterWithChainInformation:
                    case AnnouncementTypeEnum.ManualBovineSlaughter:
                    case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                    case AnnouncementTypeEnum.SlaughterWithChainInformation:
                        slaughterAnnouncements.push(announcement);
                        break;
                    case AnnouncementTypeEnum.Intermediation:
                    case AnnouncementTypeEnum.ManualBovineIntermediation:
                    case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                        intermediationAnnouncements.push(announcement);
                        break;
                    case AnnouncementTypeEnum.BovinePurchase:
                    case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                        bovinePurchaseAnnouncements.push(announcement);
                        break;
                }
            }
        }
        setSlaughterAnnouncements(slaughterAnnouncements);
        setIntermediationAnnouncements(intermediationAnnouncements);
        setBovinePurchaseAnnouncements(bovinePurchaseAnnouncements);
    }, [animalAnnouncementData]);

    useEffect(() => {
        if (slaughterAnnouncements.length > 0) {
            setSlaughterAnnouncementCount(slaughterAnnouncements.length);
            setAnimalCountInAnnouncement(slaughterAnnouncements, AnnouncementTypeEnum.Slaughter);

            initialProducerReturnDataFetch();
        } else if (slaughterAnnouncements.length === 0) {
            getAnnouncementCounterDefaultValues(AnnouncementTypeEnum.Slaughter);
        }
    }, [slaughterAnnouncements]);

    useEffect(() => {
        if (intermediationAnnouncements.length > 0) {
            setIntermediationAnnouncementCount(intermediationAnnouncements.length);
            setAnimalCountInAnnouncement(
                intermediationAnnouncements,
                AnnouncementTypeEnum.Intermediation
            );
        } else if (intermediationAnnouncements.length === 0) {
            getAnnouncementCounterDefaultValues(AnnouncementTypeEnum.Intermediation);
        }
    }, [intermediationAnnouncements]);

    useEffect(() => {
        if (bovinePurchaseAnnouncements.length > 0) {
            setBovinePurchaseAnnouncementCount(bovinePurchaseAnnouncements.length);
            setAnimalCountInAnnouncement(
                bovinePurchaseAnnouncements,
                AnnouncementTypeEnum.BovinePurchase
            );
        } else if (bovinePurchaseAnnouncements.length === 0) {
            getAnnouncementCounterDefaultValues(AnnouncementTypeEnum.BovinePurchase);
        }
    }, [bovinePurchaseAnnouncements]);

    useEffect(() => {
        let data = filteredAnnouncementData();
        let show = !!data && !!selectedParamFilter;
        setShowAnnouncementListGrid(show);
    }, [
        selectedParamFilter,
        selectedParamFilter?.paramCode,
        selectedParamFilter?.subParam,
        animalAnnouncementData,
        bovinePurchaseAnnouncements,
        intermediationAnnouncements,
    ]);

    // AN-2614
    const checkNeedForPreliminaryAnimalAnnouncementWarnings = (
        announcements: IAnimalAnnouncement[]
    ) => {
        // Remove possibly previously added infos that relates to animal announcements
        let currentCarouselInfo = [...additionalInfo].filter(
            (info) => info.BoxId !== 5 && info.BoxId !== 6
        );

        const preliminaryTypes = [
            AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement,
            AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement,
            AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement,
        ];

        const filteredAnnouncements = announcements?.filter(
            (announcement) =>
                preliminaryTypes.includes(announcement.Type)
                && (announcement.ScheduledFrom == null
                    || moment(announcement.ScheduledFrom) < moment())
        );

        if (filteredAnnouncements?.length > 0) {
            addPreliminaryAnnouncementWarnings(currentCarouselInfo, filteredAnnouncements);
        }
        else {
            setAdditionalInfo(currentCarouselInfo);
        }
    };

    const addPreliminaryAnnouncementWarnings = (currentCarouselInfo: ACarouselAdditionalBoxInfo[], announcements: IAnimalAnnouncement[]) => {
        for (const announcement of announcements) {
            const currentInfo = currentCarouselInfo.find(
                (i) => isNotNil(i.RelatedItemId) && i.RelatedItemId === announcement.Id
            );
            // If no warnings about the current animal announcement apply one
            if (currentInfo === undefined) {
                currentCarouselInfo.push({
                    BoxId: announcement.Type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement ? 6 : 5,
                    IconInfo: {
                        Info:
                            announcement.Type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                                ? CarouselInfoEnum.UnconfirmedPreliminaryBovinePurchaseAnnouncement
                                : CarouselInfoEnum.UnconfirmedPreliminaryAnnouncement,
                    },
                    RelatedItemId: announcement.Id,
                });
            }
        }

        setAdditionalInfo(currentCarouselInfo);
    };

    const handleUpdatedSalesType = (data: IFarmAnimal[]) => {
        if (allAnimalsData !== null) {
            let currentAnimalsData = [...allAnimalsData];
            for (const changedAnimal of data) {
                for (const dataRow of currentAnimalsData) {
                    for (const animal of dataRow.Animals) {
                        if (changedAnimal.AnimalGUID === animal.Id) {
                            animal.SalesType = changedAnimal.SalesType;
                        }
                    }
                }
            }
            setAllAnimalsData(currentAnimalsData);
        }
    };

    const removeAnimal = (animal: IFarmAnimal) => {
        if (!animal || !animal.ValidTo) return;

        if (allAnimalsData !== null) {
            let currentAnimalsData = [...allAnimalsData];
            var animalToRemove;

            for (const l of currentAnimalsData) {
                if (l.FilterType === AnimalFilterTypeEnum.All) {
                    animalToRemove = l.Animals.find((a) => a.Id === animal.AnimalGUID);
                    break;
                }
            }

            for (const l of currentAnimalsData) {
                if (l.FilterType === AnimalFilterTypeEnum.Deceased) {
                    if (animalToRemove) {
                        animalToRemove.Flags = 0;
                        l.Animals.push(animalToRemove);
                        l.AnimalCount += 1;
                    }
                } else {
                    l.Animals = l.Animals.filter((a) => a.Id !== animal.AnimalGUID);
                    l.AnimalCount = l.Animals.length;
                }
            }

            setAllAnimalsData(currentAnimalsData);
        }
    };

    const removeAnimalsFromWaitingRoom = (ids: string[]) => {
        setAllAnimalsData((previous) => {
            if (previous === null)
                return null;

            let res = previous.map(f => {
                const newAnimals = f.Animals
                    .map(a => {
                        const updated = { ...a };
                        if (ids.includes(a.Id)) {
                            updated.Flags = updated.Flags & ~(1) & ~(2);
                        }
                        return updated;
                    })
                    .filter(a =>
                        f.FilterType === AnimalFilterTypeEnum.WaitingRoom
                            ? !ids.includes(a.Id)
                            : true
                    );

                return {
                    ...f,
                    AnimalCount: newAnimals.length,
                    Animals: newAnimals,
                };
            })

            return res;
        });
    };

    const getRelatedAnnouncement = (childId: string) => {
        let i = animalAnnouncementData.findIndex(a =>
            a.RelatedAnnouncement !== null
            && a.RelatedAnnouncement === childId);

        if (i != -1)
            return animalAnnouncementData[i];

        return null;
    }

    const getAnnouncementCounterDefaultValues = (type: AnnouncementTypeEnum) => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
                setSlaughterAnnouncementCount(0);
                setSlaughterAnnouncementAnimalCount(0);
                break;
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
                setIntermediationAnnouncementCount(0);
                setIntermediationAnnouncementAnimalCount(0);
                break;
            case AnnouncementTypeEnum.BovinePurchase:
                setBovinePurchaseAnnouncementCount(0);
                setBovinePurchaseAnnouncementAnimalCount(0);
                break;
        }
    };

    const setAnimalCountInAnnouncement = (
        animalAnnouncement: IAnimalAnnouncement[],
        type: AnnouncementTypeEnum
    ): number => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
            case AnnouncementTypeEnum.ManualBovineSlaughter:
                setSlaughterAnnouncementAnimalCount(getAnimalCount(animalAnnouncement));
                break;
            case AnnouncementTypeEnum.Intermediation:
            case AnnouncementTypeEnum.ManualBovineIntermediation:
                setIntermediationAnnouncementAnimalCount(getAnimalCount(animalAnnouncement));
                break;
            case AnnouncementTypeEnum.BovinePurchase:
                setBovinePurchaseAnnouncementAnimalCount(
                    getBovinePurchaseAnimalCount(animalAnnouncement)
                );
                break;
        }
        return 0;
    };

    const initialAnnouncementDataFetch = async () => {
        if (farmData !== null) {
            let farmGuid = String(farmData.Id);
            await animalAnnouncementApi
                .getAnimalAnnouncementsFromAnelma(farmGuid)
                .then((response) => {
                    if (!response) {
                        log(
                            'info',
                            `getAnimalAnnouncementsFromAnelma response returned no results.`
                        );
                        return;
                    }
                    let filtered = response.Items.filter(
                        (a) =>
                            a.Status !== 'LO' &&
                            a.Batches.findIndex((b) => b.BatchStatus != 'LO') != -1
                    );
                    setAnimalAnnouncementData(filtered);
                });
        }
    };

    const refreshAnnouncements = async () => {
        await initialAnnouncementDataFetch();
    }

    const initialProducerReturnDataFetch = async () => {
        producerReturnApi
            .getProducerReturnListByAnnouncements(slaughterAnnouncements.map(a => a.Id))
            .then((response) => {
                if (!response) {
                    log(
                        'error',
                        `getProducerReturnListByAnnouncements failed.`
                    );
                    return;
                }

                setProducerReturns(response.Items);
            });
    };

    const fetchGroupsData = () => {
        if (farmData?.Id) {
            setIsLoading(true);
            groupsApi
                .getGroups(String(farmData.Id))
                .then((response) => {
                    if (response?.Items) {
                        setAnimalGroups(response.Items);
                    }
                })
                .catch((err) => {
                    log('error', `groupsApi.getGroups error: ${err}`);
                    return;
                })
                .finally(() => setIsLoading(false));
        }
    };

    const closeSlaughterAnnouncementDialog = (openPurchaseForConfirmation?: boolean) => {
        initialProducerReturnDataFetch();
        setAnnouncementDialogVisible(false);

        if (openPurchaseForConfirmation)
            openConfirmBovinePurchaceAnnouncement();
    }

    const handleAnnouncementDialogVisible = (
        item: IAnimalAnnouncement,
        chainInformationFound: boolean
    ) => {
        setChainInformationFound(chainInformationFound);
        setSelectedAnnouncement(item);
        setAnnouncementDialogVisible(true);
    };

    const handleAnnouncementDeletionFromTiltu = (announcementNumber: number) => {
        setIsLoading(true);
        setAnimalAnnouncementData(
            animalAnnouncementData.filter((_) => _.Number !== announcementNumber)
        );
        setIsLoading(false);
    };

    const handleAnnouncementDeletionFromTiltuById = (announcementId: GUIDType) => {
        setIsLoading(true);
        setAnimalAnnouncementData(animalAnnouncementData.filter((_) => _.Id !== announcementId));
        setIsLoading(false);
    };

    const handleProducerReturnsDeletion = (announcementId: GUIDType) => {
        setProducerReturns((prev) => prev.filter((r) => r.AnnouncementId !== announcementId));
    };

    const mapAnimalAnnouncementBatchToIAnimalSummary = (
        selectionModel: GridRowSelectionModel
    ): IAnimalSummaryExtraInfo[] => {
        if (selectionModel.length > 0) {
            let selectedAnimalAnnouncementData: IAnimalAnnouncement[] = [];
            for (const selectionRow of selectionModel) {
                for (const announcement of [...animalAnnouncementData]) {
                    if ((selectionRow.valueOf() as string) === announcement.Id)
                        selectedAnimalAnnouncementData.push(announcement);
                }
            }

            const tempAllAnimas =
                (allAnimalsData?.find((a) => a.FilterType === AnimalFilterTypeEnum.All)
                    ?.Animals as IAnimalSummaryExtraInfo[]) ?? [];

            return mapAnimalAnnouncementAnimalsToIAnimalSummaryExtraInfo(
                selectedAnimalAnnouncementData,
                tempAllAnimas,
                animalGroups
            );
        }
        return [];
    };

    const handleSelectedAnnouncement = (selectionModel: GridRowSelectionModel) => {
        setAnimalListGridProps({
            allAnimalsData: allAnimalsData,
            animalData: mapAnimalAnnouncementBatchToIAnimalSummary(selectionModel),
            animalGroups: animalGroups,
            setAnimalGroups: setAnimalGroups,
            selectedParamFilter: selectedParamFilter,
            isLoading: isLoading,
            handleAnimalAnnouncementMapToState: (data: IAnimalAnnouncement, type: AnnouncementTypeEnum) =>
                mapNewAnnouncementToState(data, type),
            mapUpdatedSalesTypeToAllAnimalsData: (data: IFarmAnimal[]) =>
                handleUpdatedSalesType(data),
            processAnnouncementDelete: handleAnnouncementDeletionFromTiltuById,
            farmAnnouncements: animalAnnouncementData,
            farmData: farmData ?? null,
            producerReturns: producerReturns,
            canCreateAnimalAnnouncement: canCreateAnimalAnnouncement,
            removeAnimal: removeAnimal,
            removeAnimalsFromWaitingRoom: removeAnimalsFromWaitingRoom,
            refreshAnnouncements: refreshAnnouncements,
        });
    };

    const handleAnnouncementReload = (id: GUIDType) => {
        if (farmData?.Id)
            animalAnnouncementApi.getAnimalAnnouncementsFromAnelma(farmData?.Id).then((res) => {
                for (let item of res?.Items || []) {
                    if (item.Id === id) {
                        const index = animalAnnouncementData.findIndex((x) => x.Id === id);
                        if (index !== -1) {
                            animalAnnouncementData[index] = item;
                            setAnimalAnnouncementData([...animalAnnouncementData]);
                        } else {
                            setAnimalAnnouncementData([item, ...animalAnnouncementData]);
                        }
                        return;
                    }
                }
            });
    };

    const updateAnnouncementState = (animalAnnouncement: IAnimalAnnouncement) => {
        const animalAnnouncementBeforeUpdate = animalAnnouncementData.filter(
            (_) =>
                _.Id ===
                [...animalAnnouncementData].find((data) => {
                    return data.Id === animalAnnouncement.Id;
                })?.Id
        );
        // [AN-1279] Preserve some of the current field values. Made because of Created-value is not returned from API/Tiltu.
        const updatedObject = animalAnnouncementBeforeUpdate.map((_) => {
            if (_.Id === animalAnnouncement.Id)
                return {
                    Id: _.Id, // Does not change
                    Number: animalAnnouncement.Number, // Changes for preliminary announcements
                    Type: animalAnnouncement.Type,
                    Created: _.Created, // Cannot change
                    ScheduledFrom: _.ScheduledFrom, // Cannot change
                    HoldingSiteId: animalAnnouncement.HoldingSiteId,
                    Info: animalAnnouncement.Info,
                    Status: animalAnnouncement.Status,
                    Batches: animalAnnouncement.Batches,
                    HasFiles: animalAnnouncement.HasFiles,
                    Flags: animalAnnouncement.Flags,
                };
        });
        const updatedAnimalAnnouncementData = animalAnnouncementData.filter(
            (_) =>
                _.Id !==
                [...animalAnnouncementData].find((data) => {
                    return data.Id === updatedObject[0]?.Id;
                })?.Id
        );
        if (
            updatedObject[0] &&
            isNotNil(updatedObject[0]?.Flags) &&
            updatedObject[0].Flags === AnnouncementFlagsEnum.Deleted
        ) {
            setAnimalAnnouncementData(updatedAnimalAnnouncementData);
        } else {
            updatedAnimalAnnouncementData.push(updatedObject[0] as IAnimalAnnouncement);
            setAnimalAnnouncementData(updatedAnimalAnnouncementData);
        }
    };

    const mapNewAnnouncementToState = (data: IAnimalAnnouncement, type: AnnouncementTypeEnum) => {
        switch (type) {
            case AnnouncementTypeEnum.Slaughter:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
            case AnnouncementTypeEnum.Intermediation:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
            case AnnouncementTypeEnum.BovinePurchase:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
            case AnnouncementTypeEnum.ManualBovineSlaughter:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
            case AnnouncementTypeEnum.ManualBovineIntermediation:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
            case AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                handleAnnouncementAnimalUpdates(data);

                break;
            case AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                handleAnnouncementAnimalUpdates(data);

                break;
            case AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement:
                setAnimalAnnouncementData((previousState) => [
                    ...previousState,
                    data,
                ]);
                break;
        }
    };

    // This is used when modifications are made to the announcement
    const handleAnnouncementUpdate = (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string
    ) => {

        if (type !== AnnouncementTypeEnum.BovinePurchase
            && type !== AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
            && type !== AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
            && type !== AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
        ) {
            // announcement saving can affect other announcements
            // so we want to refresh the whole list
            refreshAnnouncements();
        }
        else
            updateAnnouncementState(mapAnnouncementResult(data, holdingSiteId, type));
    };

    const handleAnnouncementAnimalUpdates = (data: IAnimalAnnouncement) => {
        // handle all the changes at once 
        // to avoid multiple state updates
        // that may cause race conditions
        setAllAnimalsData((previous) => {
            if (previous === null)
                return null;

            const ids = data.Batches.map(b => b.Animals[0].AnimalId);

            let res = previous.map(f => {
                const newAnimals = f.Animals
                    .map(a => {
                        const updated = { ...a };
                        if (ids.includes(a.Id)) {
                            // remove "waiting" flags
                            updated.Flags = updated.Flags & ~(1) & ~(2);

                            // set default sales type for announcement animals in animal list
                            let animal = data.Batches.find(b => b.Animals[0].AnimalId === a.Id)?.Animals[0];
                            if (animal)
                                updated.SalesType = animal.SalesType;
                        }
                        return updated;
                    })
                    // remove animals from waiting room after preliminary announcement creation
                    // to avoid multiple preliminary announcements for the same animal
                    .filter(a =>
                        f.FilterType === AnimalFilterTypeEnum.WaitingRoom
                            ? !ids.includes(a.Id)
                            : true
                    );

                return {
                    ...f,
                    AnimalCount: newAnimals.length,
                    Animals: newAnimals,
                };
            })

            return res;
        });
    }

    const getAllAnimals = () => {
        let i = -1;

        if (allAnimalsData) {
            i = allAnimalsData.findIndex(a => a.FilterType === AnimalFilterTypeEnum.All);

            if (i != -1)
                return allAnimalsData[i].Animals;
        }

        return [];
    }

    const countExternalFields = (animals: IAnimalSummaryExtraInfo[]) => {
        if (animals !== null) {
            let res = [...animals];

            res.forEach((a, i) => {
                a.Eu4 =
                    a.EuIdentifier?.length > 0
                        ? a.EuIdentifier.substring(a.EuIdentifier.length - 6, a.EuIdentifier.length - 2)
                        : null;

                a.AgeMonths = utils.calculateAgeInMonths(moment(), moment(a.BirthDate), 30.33);
                a.Group = getAnimalGroup(a.Id, animalGroups);
            });

            return res;
        }

        return null;
    }

    const handleShowSnackbarGeneralError = () => {
        enqueueSnackbar(t('AnelmaGeneral:1020'), {
            variant: 'error',
        });
    };

    const filteredAnnouncementData = useCallback(() => {
        if (!selectedParamFilter) return [];

        /* Bovine purchase animal announcement data main view and dropdown selection based view */
        if (
            selectedParamFilter.paramCode === 6 &&
            (selectedParamFilter.subParam === 6 || selectedParamFilter.subParam === 23)
        )
            return bovinePurchaseAnnouncements;

        if (selectedParamFilter.paramCode === 5) {
            /* 'Myynti-ilmoitukset' carousel view. Consists of slaughter and bovine intermediation announcements */
            if (selectedParamFilter.subParam === 5)
                return omitBovinePurchaseAnnouncements(animalAnnouncementData);

            /* Slaughter animal announcement data only */
            if (selectedParamFilter.subParam === 17) return slaughterAnnouncements;

            /* Intermediation animal announcement data only */
            if (selectedParamFilter.subParam === 22) return intermediationAnnouncements;
        }
        return null;
    }, [
        selectedParamFilter,
        selectedParamFilter?.paramCode,
        selectedParamFilter?.subParam,
        animalAnnouncementData,
        bovinePurchaseAnnouncements,
        intermediationAnnouncements,
    ]);

    const announcementListGridColumnType = useCallback(() => {
        if (
            selectedParamFilter?.paramCode === 6 &&
            (selectedParamFilter.subParam === 6 || selectedParamFilter.subParam === 23)
        )
            return AnnouncementListDataGridColumnType.BovinePurchase;

        if (selectedParamFilter?.paramCode === 5 && selectedParamFilter.subParam === 22)
            return AnnouncementListDataGridColumnType.BovineIntermediation;

        return AnnouncementListDataGridColumnType.Default;
    }, [selectedParamFilter, selectedParamFilter?.paramCode, selectedParamFilter?.subParam]);

    const openConfirmBovinePurchaceAnnouncement = () => {
        if (selectedAnnouncement) {
            animalAnnouncementApi
                .getRelatedAnnouncement(selectedAnnouncement.Id)
                .then((response) => {
                    if (!response) {
                        handleShowSnackbarGeneralError();
                        return;
                    }

                    if (response.Entity)
                        setConfirmBovinePurchaseAnnouncement({
                            announcement: response.Entity,
                            openDialog: true,
                        });
                });
        }
    };

    return (
        <Container id='anelma-animals-animal-list-container'>
            <ViewTitle>{t('AnelmaLayout:1010')}</ViewTitle>

            {animalListFilterProps !== null && (
                <AnimalListFilter {...animalListFilterProps} data-robot-id={'animal-list-filter'} />
            )}
            {selectedParamFilter && (
                <>
                    {isLoading && <ViewLoader />}
                    {!isLoading && showAnnouncementListGrid && announcementListGridProps && (
                        <AnnouncementListDataGrid
                            key={'announcement-list'}
                            {...announcementListGridProps}
                            data-robot-id={'animal-list-announcement-list'}
                        />
                    )}
                    {!isLoading && animalListGridProps && (
                        <AnimalListGrid
                            key={'animal-list'}
                            {...animalListGridProps}
                            data-robot-id={'animal-list-grid'}
                        />
                    )}
                </>
            )}

            {announcementDialogVisible && (
                <>
                    {(selectedAnnouncement?.Type === AnnouncementTypeEnum.BovinePurchase ||
                        selectedAnnouncement?.Type ===
                            AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement) && (
                        <BovinePurchaseAnnouncement
                            data-robot-id={
                                'bovine-purchase-announcement-' + selectedAnnouncement.Type
                            }
                            data={selectedAnnouncement}
                            onClose={() => setAnnouncementDialogVisible(false)}
                            mode={DialogModeEnum.Edit}
                            processAnnouncementSave={handleAnnouncementUpdate}
                            processAnnouncementDelete={handleAnnouncementDeletionFromTiltuById}
                            announcementConfirmation={
                                selectedAnnouncement.Type ===
                                AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                            }
                            readonly={isAnimalAnnouncementUpdateRestricted(selectedAnnouncement)}
                            weekReadonly={
                                selectedAnnouncement.Type ===
                                AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                            }
                        />
                    )}
                    {(selectedAnnouncement?.Type === AnnouncementTypeEnum.Slaughter ||
                        selectedAnnouncement?.Type ===
                            AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement) && (
                        <BovineSlaughterAnnouncement
                            data-robot-id={
                                'bovine-slaughter-announcement-' + selectedAnnouncement.Type
                            }
                            data={selectedAnnouncement}
                            onClose={(openPurchaseForConfirmation?: boolean) => closeSlaughterAnnouncementDialog(openPurchaseForConfirmation)}
                            mode={DialogModeEnum.Edit}
                            selectedAnimals={null}
                            animals={countExternalFields(getAllAnimals() as IAnimalSummaryExtraInfo[])}
                            processAnnouncementSave={handleAnnouncementUpdate}
                            processAnnouncementDelete={handleAnnouncementDeletionFromTiltuById}
                            mapUpdatedSalesTypeToAllAnimalsData={handleUpdatedSalesType}
                            loading={announcementActionLoading}
                            canCreateAnimalAnnouncement={canCreateAnimalAnnouncement}
                            readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnnouncement)}
                            chainInformationFound={chainInformationFound}
                            announcementConfirmation={
                                selectedAnnouncement.Type ===
                                AnnouncementTypeEnum.PreliminaryBovineSlaughterAnnouncement
                            }
                        />
                    )}
                    {(selectedAnnouncement?.Type === AnnouncementTypeEnum.Intermediation ||
                        selectedAnnouncement?.Type ===
                            AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement) && (
                        <BovineIntermediationAnnouncement
                            data-robot-id={
                                'bovine-intermediation-announcement-' + selectedAnnouncement.Type
                            }
                            data={selectedAnnouncement}
                            onClose={(openPurchaseForConfirmation?: boolean) => {
                                setAnnouncementDialogVisible(false);

                                if (openPurchaseForConfirmation)
                                    openConfirmBovinePurchaceAnnouncement();
                            }}
                            mode={DialogModeEnum.Edit}
                            selectedAnimals={null}
                            animals={countExternalFields(getAllAnimals() as IAnimalSummaryExtraInfo[])}
                            processAnnouncementSave={handleAnnouncementUpdate}
                            processAnnouncementDelete={handleAnnouncementDeletionFromTiltuById}
                            mapUpdatedSalesTypeToAllAnimalsData={handleUpdatedSalesType}
                            loading={announcementActionLoading}
                            canCreateAnimalAnnouncement={canCreateAnimalAnnouncement}
                            readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnnouncement)}
                            chainInformationFound={chainInformationFound}
                            announcementConfirmation={
                                selectedAnnouncement.Type ===
                                AnnouncementTypeEnum.PreliminaryBovineIntermediationAnnouncement
                            }
                        />
                    )}
                    {selectedAnnouncement?.Type === AnnouncementTypeEnum.ManualBovineSlaughter && (
                        <ManualAnnouncementDialog
                            data-robot-id={
                                'bovine-manual-slaughter-announcement-' + selectedAnnouncement.Type
                            }
                            announcementData={selectedAnnouncement}
                            announcementType={AnnouncementTypeEnum.ManualBovineSlaughter}
                            mode={DialogModeEnum.Edit}
                            onClose={() => closeSlaughterAnnouncementDialog()}
                            processAnnouncementSave={handleAnnouncementUpdate}
                            mapUpdatedSalesTypeToAllAnimalsData={handleUpdatedSalesType}
                            animals={countExternalFields(getAllAnimals() as IAnimalSummaryExtraInfo[])}
                            withChainInformation={false}
                            readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnnouncement)}
                            chainInformationFound={chainInformationFound}
                        />
                    )}
                    {selectedAnnouncement?.Type ===
                        AnnouncementTypeEnum.ManualBovineIntermediation && (
                        <ManualAnnouncementDialog
                            data-robot-id={
                                'bovine-manual-intermediation-announcement-' +
                                selectedAnnouncement.Type
                            }
                            announcementData={selectedAnnouncement}
                            announcementType={AnnouncementTypeEnum.ManualBovineIntermediation}
                            mode={DialogModeEnum.Edit}
                            onClose={() => setAnnouncementDialogVisible(false)}
                            processAnnouncementSave={handleAnnouncementUpdate}
                            mapUpdatedSalesTypeToAllAnimalsData={handleUpdatedSalesType}
                            animals={countExternalFields(getAllAnimals() as IAnimalSummaryExtraInfo[])}
                            withChainInformation={false}
                            readOnly={isAnimalAnnouncementUpdateRestricted(selectedAnnouncement)}
                            chainInformationFound={chainInformationFound}
                        />
                    )}
                </>
            )}
            {confirmBovinePurchaseAnnouncement.openDialog && (
                <ConfirmBovinePurchaseAnnouncement
                    announcement={confirmBovinePurchaseAnnouncement.announcement}
                    onClose={() => setConfirmBovinePurchaseAnnouncement({ announcement: null, openDialog: false })}
                    processAnnouncementDelete={handleAnnouncementDeletionFromTiltuById}
                    processAnnouncementSave={handleAnnouncementUpdate}
                />
            )}
        </Container>
    );
}
